import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';

interface ZeppelinPosition {
  x: number;
  y: number;
}

interface ZeppelinPositionContextValue {
  position: ZeppelinPosition | null;
  updatePosition: (rect: DOMRect | null) => void;
}

const ZeppelinPositionContext = createContext<ZeppelinPositionContextValue | undefined>(undefined);

export function ZeppelinPositionProvider({ children }: { children: ReactNode }) {
  const [position, setPosition] = useState<ZeppelinPosition | null>(null);
  const lastUpdateRef = useRef<number>(0);

  const updatePosition = useCallback((rect: DOMRect | null) => {
    const now = performance.now();
    if (now - lastUpdateRef.current < 16) return;
    lastUpdateRef.current = now;

    if (!rect) {
      setPosition(null);
      return;
    }

    const newPos = {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
    
    setPosition(newPos);
  }, []);

  return (
    <ZeppelinPositionContext.Provider value={{ position, updatePosition }}>
      {children}
    </ZeppelinPositionContext.Provider>
  );
}

export function useZeppelinPosition() {
  const context = useContext(ZeppelinPositionContext);
  if (!context) {
    return { position: null, updatePosition: () => {} };
  }
  return context;
}
